/**
 * Cart quantity +/- buttons (cart page).
 * Updates quantity, line price, and subtotal in the DOM via AJAX — no full-page reload.
 * The cart drawer has its own handler in cart-drawer.js.
 *
 * @typedef {{ formatMoney?: (cents: number) => string }} ShopifyGlobal
 * @typedef {{ routes?: { cartChange?: string }, strings?: Record<string, string>, cartType?: string }} ThemeSettings
 * @typedef {{ publish: (event: string) => void }} AlverioGlobal
 */

/** @param {number} cents */
function formatMoney(cents) {
  /** @type {ShopifyGlobal | undefined} */
  const shopify = /** @type {any} */ (window).Shopify;
  if (shopify && typeof shopify.formatMoney === 'function') {
    return shopify.formatMoney(cents);
  }
  return '$' + (cents / 100).toFixed(2);
}

/** @param {HTMLElement} btn */
function getKeyFromButton(btn) {
  const row = btn.closest('[data-key]');
  return row instanceof HTMLElement ? row.getAttribute('data-key') : null;
}

/** @param {HTMLElement} btn @returns {number | null} */
function getQtyFromButton(btn) {
  const row = btn.closest('[data-key]');
  if (!(row instanceof HTMLElement)) return null;
  const qtyEl = row.querySelector('[data-cart-qty-value]');
  if (!(qtyEl instanceof HTMLElement)) return null;
  const value = Number(String(qtyEl.textContent || '').trim());
  return Number.isFinite(value) ? value : null;
}

/** @param {HTMLElement} btn */
function inDrawer(btn) {
  return Boolean(btn.closest('#CartDrawer'));
}

/**
 * @param {string} id
 * @param {number} quantity
 */
async function changeQty(id, quantity) {
  /** @type {ThemeSettings | undefined} */
  const settings = /** @type {any} */ (window).themeSettings;
  const base = settings?.routes?.cartChange || '/cart/change';
  const res = await fetch(base + '.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id, quantity }),
  });
  if (!res.ok) throw new Error('Cart change failed');
  return /** @type {Promise<any>} */ (res.json());
}

document.addEventListener('click', async (event) => {
  const target = event.target instanceof HTMLElement ? event.target : null;
  if (!target) return;

  const btn = target.closest('[data-cart-qty-minus],[data-cart-qty-plus]');
  if (!(btn instanceof HTMLElement)) return;

  if (inDrawer(btn)) return;

  const key = getKeyFromButton(btn);
  const current = getQtyFromButton(btn);
  if (!key || current === null) return;

  const delta = btn.hasAttribute('data-cart-qty-plus') ? 1 : -1;
  const next = Math.max(0, current + delta);
  const row = btn.closest('[data-key]');

  btn.setAttribute('disabled', 'disabled');
  try {
    const cart = await changeQty(key, next);

    if (next === 0) {
      row?.remove();
    } else {
      const qtyEl = row?.querySelector('[data-cart-qty-value]');
      if (qtyEl) qtyEl.textContent = String(next);

      const updatedItem = Array.isArray(cart.items)
        ? cart.items.find(/** @param {any} i */ (i) => i.key === key)
        : null;
      const priceEl = row?.querySelector('[data-cart-item-price]');
      if (priceEl && updatedItem) {
        priceEl.textContent = formatMoney(updatedItem.final_line_price);
      }
    }

    const subtotalEl = document.querySelector('[data-cart-subtotal]');
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    /** @type {AlverioGlobal | undefined} */
    const alverio = /** @type {any} */ (window).Alverio;
    if (alverio) alverio.publish('cart:updated');

    if (cart.item_count === 0) {
      window.location.reload();
    }
  } catch {
    // noop — quantity display unchanged; user can retry
  } finally {
    btn.removeAttribute('disabled');
  }
});
